// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductCarouselProduct from "@insite/content-library/Components/ProductCarouselProduct";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Tab, { TabPresentationProps } from "@insite/mobius/Tab";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TitleTypography from "@keller/App/Components/TitleTypography/TitleTypography";
import XngCarousel, { SwiperStyle } from "@keller/App/Components/XngCarousel/XngCarousel";
import { CustomAppState } from "@keller/App/Store/Reducers";
import ShopByProductItem from "@keller/App/Widgets/ShopByProduct/ShopByProductItem";
import TabGroup, { TabGroupPresentationProps } from "@keller/MobiusOverrides/TabGroup";
import { constants } from "@keller/XDL/constants";
import { xScreen } from "@keller/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum tabFields {
    title = "title",
    products = "products",
}

const enum fields {
    tabs = "tabs",
    title = "title",
}

interface TabsProps {
    fields: {
        title: string;
        products: {
            fields: {
                productId: string;
            };
        }[];
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.tabs]: TabsProps[];
    };
}

const mapStateToProps = (state: CustomAppState) => {
    return {
        isTabletScreen: state.xCommon.isTabletScreen,
    };
};

const mapDispatchToProps = {
    loadProductInfoList,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ContentFeatureStyles {
    shopByGridContainer?: GridContainerProps;
    shopByGridItem?: GridItemProps;
    title?: TypographyProps;
    centeringWrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    tabGroupStyle?: TabGroupPresentationProps;
    tabStyle?: TabPresentationProps;
    swiperStyles?: SwiperStyle;
}

const { productGridView: productGridViewScreen } = constants;
export const shopByStyles: ContentFeatureStyles = {
    shopByGridContainer: {
        gap: 20,
    },
    shopByGridItem: {
        width: [12, 6, 6, 4, 4],
        css: css`
            ${xScreen("lte", "xs")} {
                justify-content: center;
            }
        `,
    },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            flex-direction: column;
        `,
    },
    innerWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: flex-start;
        `,
    },
    tabGroupStyle: {
        mergeCss: true,
        cssOverrides: {
            tabContent: css`
                padding: 16px 12px 5px;
                ${xScreen("lte", "md")} {
                    padding: 0;
                    border-width: 0;
                    background-color: transparent;
                    box-shadow: none;
                }
            `,
        },
    },
    swiperStyles: {
        container: {
            css: css`
                .swiper-slide {
                    width: ${productGridViewScreen.desktop.width}px;
                    ${xScreen("lte", "md")} {
                        width: ${productGridViewScreen.tablet.width}px;
                    }

                    ${xScreen("lte", "xs")} {
                        width: ${productGridViewScreen.mobile.width}px;
                    }
                }
            `,
        },
    },
};

const styles = shopByStyles;

const ShopByProduct: FC<Props> = ({ id, fields, loadProductInfoList, isTabletScreen }) => {
    const products = useAppSelector(state => getProductsForProductInfoList(state, id));
    const [tabProducts, setTabProducts] = useState<{ title: string; products: string[] }[]>([]);
    const shellContext = useShellContext();
    const [currentTab, setCurrentTab] = useState("");

    useEffect(() => {
        const productsIdsByTab: typeof tabProducts = [];
        const allProductIds: string[] = [];

        fields.tabs.forEach(tab => {
            const { fields } = tab;
            const tabProductsIds = fields.products?.map(product => {
                allProductIds.push(product.fields.productId);
                return product.fields.productId;
            });

            // Hide tab when it doesn't have products in CMS
            if (tabProductsIds.length > 0) {
                productsIdsByTab.push({ title: fields.title, products: tabProductsIds });
            }
        });

        setTabProducts(productsIdsByTab);
        setCurrentTab(productsIdsByTab?.[0]?.title);

        if (allProductIds.length === 0) {
            return;
        }

        loadProductInfoList({
            id,
            getProductCollectionParameter: { productNumbers: allProductIds, pageSize: "9999" } as any,
        });
    }, [fields]);

    const onTabChange = (event: React.MouseEvent | React.KeyboardEvent, tabKey?: string) => {
        if (tabKey) {
            setCurrentTab(tabKey);
        }
    };

    if (tabProducts.length === 0 || !products || products?.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.centeringWrapper} data-test-selector="ShopByProduct">
            {fields.title && <TitleTypography {...styles.title}>{fields.title}</TitleTypography>}

            <TabGroup
                current={tabProducts[0]?.title}
                selectLabel={translate("Product Categories")}
                onTabChange={onTabChange}
                {...styles.tabGroupStyle}
            >
                {tabProducts
                    .filter(tab => {
                        const productsByTab = products?.filter(product =>
                            tab.products?.includes(product.productNumber),
                        );
                        return productsByTab.length > 0 || shellContext.isEditing;
                    })
                    .map(tab => {
                        const productsByTab = products?.filter(product =>
                            tab.products?.includes(product.productNumber),
                        );
                        return (
                            <Tab key={tab.title} headline={tab.title} tabKey={tab.title} {...styles.tabStyle}>
                                {isTabletScreen ? (
                                    <>
                                        {
                                            currentTab === tab.title && (
                                                <XngCarousel
                                                    navigation={true}
                                                    spaceBetween={22}
                                                    slidesPerView="auto"
                                                    extendedStyles={styles.swiperStyles}
                                                >
                                                    {productsByTab?.map(product => (
                                                        <ProductCarouselProduct
                                                            key={product.id}
                                                            data-test-key={product.id}
                                                            data-test-selector="carousel_slide"
                                                            carouselId={id}
                                                            product={product}
                                                            showImage={true}
                                                            showBrand={true}
                                                            showTitle={true}
                                                            showPartNumbers={true}
                                                            showPrice={true}
                                                            showAddToCart={true}
                                                            showAddToList={true}
                                                        />
                                                    ))}
                                                </XngCarousel>
                                            )
                                        }
                                    </>
                                ) : (
                                    <GridContainer {...styles.shopByGridContainer}>
                                        {productsByTab?.map(product => (
                                            <GridItem {...styles.shopByGridItem} key={product.id}>
                                                <ShopByProductItem product={product} widgetId={id} />
                                            </GridItem>
                                        ))}
                                    </GridContainer>
                                )}
                            </Tab>
                        );
                    })}
            </TabGroup>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ShopByProduct),
    definition: {
        displayName: "Shop By Product",
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.tabs,
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: tabFields.title,
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: tabFields.products,
                        editorTemplate: "ListField",
                        defaultValue: [],
                        getDisplay: (item: HasFields) => {
                            return item.fields.productId;
                        },
                        fieldDefinitions: [
                            {
                                name: "productId",
                                editorTemplate: "TextField",
                                defaultValue: "",
                            },
                        ],
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
